import { DefaultApi } from "@coeff/api";
import * as Comlink from "comlink";
import { useCallback, useEffect, useRef, useState } from "react";

import type { SqlWorkerApi } from "./sqlWorker";
import {
  downloadSQLiteDatabaseV2,
  fetchSQLiteDatabaseFromS3,
  fetchPublicSQLiteDownloadUrl,
  fetchSQLiteDownloadUrl,
} from "./webDashboard";
import {
  addPerfBreadcrumb,
  classifySql,
  LOAD_SPAN_OP,
  recordCompletedSpan,
  SLOW_SQL_BREADCRUMB_MS,
  startVisibilityTracker,
  truncateSql,
} from "./webDashboardPerf";

// --------------------------------
// Types
// --------------------------------

// Note(tommy): runSQL function is actually currently synchronous. However, to make it easier
// to migrate to async queries, we make its signature return a promise.
export type RunSQLFn = (query: string, bindings?: unknown[]) => Promise<Record<string, unknown>[]>;

type SqlWorkerRemote = Comlink.Remote<SqlWorkerApi>;

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") {
      return new Error(message);
    }
  }
  return new Error(String(error));
}

interface UseRunSQLOptions {
  spreadsheetId: string | null;
  spreadsheetVersionId: number | undefined;
  dashboardId: string | undefined;
  combinedTablesHash: string | undefined;
  apiClient: DefaultApi;
  isPublicDashboard: boolean;
  requestSuperAdminView?: boolean;
  /** Token-authorized screenshot captures receive this presigned URL with dashboard metadata. */
  sqliteDownloadUrl?: string | null;
}

interface UseRunSQLResult {
  runSQL: RunSQLFn;
  isLoading: boolean;
  isInitialized: boolean;
  error: Error | null;
  dbLoadMs: number | null;
}

type DbSourceDeps = {
  spreadsheetId: string | null;
  spreadsheetVersionId: number | undefined;
  dashboardId: string | undefined;
  combinedTablesHash: string | undefined;
  isPublicDashboard: boolean;
  requestSuperAdminView: boolean | undefined;
  sqliteDownloadUrl: string | null | undefined;
};

// --------------------------------
// Hook
// --------------------------------

export function useRunSQL({
  spreadsheetId,
  spreadsheetVersionId,
  dashboardId,
  combinedTablesHash,
  apiClient,
  isPublicDashboard,
  requestSuperAdminView,
  sqliteDownloadUrl,
}: UseRunSQLOptions): UseRunSQLResult {
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [dbLoadMs, setDbLoadMs] = useState<number | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const workerApiRef = useRef<SqlWorkerRemote | null>(null);
  const loadedDbSourceKeyRef = useRef<string | null>(null);

  const dbSourceDeps: DbSourceDeps = {
    spreadsheetId,
    spreadsheetVersionId,
    dashboardId,
    combinedTablesHash,
    isPublicDashboard,
    requestSuperAdminView,
    sqliteDownloadUrl,
  };
  const dbSourceKey = JSON.stringify(dbSourceDeps);

  const cleanupWorker = useCallback(() => {
    workerApiRef.current = null;
    workerRef.current?.terminate();
    workerRef.current = null;
    loadedDbSourceKeyRef.current = null;
  }, []);

  const cleanupWorkerIfCurrent = useCallback(
    (worker: Worker) => {
      if (workerRef.current === worker) {
        cleanupWorker();
      } else {
        worker.terminate();
      }
    },
    [cleanupWorker]
  );

  useEffect(() => {
    return () => {
      cleanupWorker();
    };
  }, [cleanupWorker]);

  // Initialize SQL.js worker and load database
  useEffect(() => {
    if (!spreadsheetId || (!spreadsheetVersionId && !dashboardId)) {
      return;
    }

    if (workerApiRef.current && loadedDbSourceKeyRef.current === dbSourceKey) {
      return;
    }

    let cancelled = false;

    const initializeDatabase = async () => {
      setIsLoading(true);
      setIsInitialized(false);
      setError(null);
      cleanupWorker();
      let worker: Worker | null = null;

      // db_load duration is wall-clock, so tab-backgrounding / laptop sleep inflate
      // it; `was_hidden` lets those outliers be filtered out of p50/p95 widgets.
      const visibility = startVisibilityTracker();

      try {
        const dbLoadStart = Date.now();

        worker = new Worker(new URL("./sqlWorker.ts", import.meta.url));
        const workerApi = Comlink.wrap<SqlWorkerApi>(worker);
        workerRef.current = worker;
        workerApiRef.current = workerApi;

        const workerWarmupPromise = workerApi.warmup();

        // Download the database — V2 direct download or V3 presigned S3 URL
        const downloadStart = Date.now();
        let arrayBuffer: ArrayBuffer;
        if (spreadsheetVersionId) {
          arrayBuffer = await downloadSQLiteDatabaseV2(spreadsheetId, spreadsheetVersionId);
        } else if (dashboardId) {
          if (sqliteDownloadUrl) {
            arrayBuffer = await fetchSQLiteDatabaseFromS3(sqliteDownloadUrl);
          } else {
            const urlResult = isPublicDashboard
              ? await fetchPublicSQLiteDownloadUrl(apiClient, dashboardId)
              : await fetchSQLiteDownloadUrl(apiClient, dashboardId, requestSuperAdminView);
            arrayBuffer = await fetchSQLiteDatabaseFromS3(urlResult.signed_url);
          }
        } else {
          throw new Error("Either spreadsheet_version_id or dashboard_id must be provided");
        }
        const downloadMs = Date.now() - downloadStart;
        const dbSizeBytes = arrayBuffer.byteLength;
        addPerfBreadcrumb("db_download", {
          dashboard_id: dashboardId,
          ms: downloadMs,
          bytes: dbSizeBytes,
        });

        if (cancelled) {
          cleanupWorkerIfCurrent(worker);
          return;
        }

        await workerWarmupPromise;
        const initStart = Date.now();
        await workerApi.initDb(Comlink.transfer(arrayBuffer, [arrayBuffer]));
        const initMs = Date.now() - initStart;
        addPerfBreadcrumb("db_init", { dashboard_id: dashboardId, ms: initMs });
        if (cancelled) {
          cleanupWorkerIfCurrent(worker);
          return;
        }

        loadedDbSourceKeyRef.current = dbSourceKey;
        const totalDbLoadMs = Date.now() - dbLoadStart;
        setDbLoadMs(totalDbLoadMs);
        // Record the whole download+init as a queryable transaction. `db_size_bytes`
        // surfaces post-refresh DB bloat, a prime suspect for slow loads.
        recordCompletedSpan({
          name: "web_dashboard.db_load",
          op: LOAD_SPAN_OP,
          startMs: dbLoadStart,
          endMs: Date.now(),
          forceTransaction: true,
          attributes: {
            dashboard_id: dashboardId,
            is_public: isPublicDashboard,
            download_ms: downloadMs,
            init_ms: initMs,
            db_size_bytes: dbSizeBytes,
            was_hidden: visibility.wasHidden(),
          },
        });
        addPerfBreadcrumb("db_ready", {
          dashboard_id: dashboardId,
          total_ms: totalDbLoadMs,
          bytes: dbSizeBytes,
        });
        setIsInitialized(true);
      } catch (err) {
        if (!cancelled) {
          console.error(err);

          // Captures the DB-unavailable case (e.g. an S3 404 while a refresh is
          // mid-swap), which renders as a "Database not initialized" message — a
          // prime suspect for a user seeing an effectively blank dashboard.
          addPerfBreadcrumb("db_load_failed", {
            dashboard_id: dashboardId,
            error: err instanceof Error ? err.message : String(err),
          });

          if (worker) {
            cleanupWorkerIfCurrent(worker);
          } else {
            cleanupWorker();
          }
          setError(toError(err));
          setIsInitialized(true); // Set true even on error to allow graceful degradation
        }
      } finally {
        visibility.stop();
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    initializeDatabase();

    return () => {
      cancelled = true;
      cleanupWorker();
    };
  }, [
    spreadsheetId,
    spreadsheetVersionId,
    dashboardId,
    combinedTablesHash,
    apiClient,
    isPublicDashboard,
    requestSuperAdminView,
    sqliteDownloadUrl,
    cleanupWorker,
    cleanupWorkerIfCurrent,
  ]);

  // Execute SQL query.
  //
  // Every dashboard SQL statement — temp-view/table creation, slicer init, and
  // block render queries — funnels through here (blocks call this same function
  // via the iframe `window.runSQL` global), so this is the one place that sees
  // all client-side SQL (it runs in a Web Worker, invisible to Sentry otherwise).
  // We emit a breadcrumb for slow/failed queries — enough to reconstruct the
  // per-query timeline in a replay — without a per-query span: at load time there
  // is no active root transaction, so per-query spans would each become their own
  // unsampled transaction (tracesSampleRate=1.0) rather than nest. Aggregate phase
  // timing lives in the web_dashboard.db_load / runtime_init transactions instead.
  const runSQL: RunSQLFn = useCallback(
    async (query: string, bindings: unknown[] = []) => {
      const workerApi = workerApiRef.current;
      if (!workerApi) {
        throw new Error("Database not initialized");
      }

      // Block render functions sometimes pass a bare param instead of an array
      // (e.g. runSQL('… WHERE x = ?', month) rather than [month]). Normalize here —
      // the one choke point every dashboard SQL statement funnels through — so a
      // non-array binding can't reach sql.js's stmt.bind() and throw.
      const normalizedBindings = Array.isArray(bindings)
        ? bindings
        : bindings == null
        ? []
        : [bindings];

      const kind = classifySql(query);
      const start = Date.now();

      try {
        const rows = await workerApi.runSQL(query, normalizedBindings);
        const durationMs = Date.now() - start;
        if (durationMs >= SLOW_SQL_BREADCRUMB_MS) {
          addPerfBreadcrumb("slow_sql", {
            dashboard_id: dashboardId,
            kind,
            ms: durationMs,
            rows: rows.length,
            query: truncateSql(query),
          });
        }
        return rows;
      } catch (err) {
        const durationMs = Date.now() - start;
        addPerfBreadcrumb("sql_error", {
          dashboard_id: dashboardId,
          kind,
          ms: durationMs,
          query: truncateSql(query),
          error: err instanceof Error ? err.message : String(err),
        });
        throw toError(err);
      }
    },
    [dashboardId]
  );

  return {
    runSQL,
    isLoading,
    isInitialized,
    error,
    dbLoadMs,
  };
}

export default useRunSQL;
